import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

// OPTIMIZED: Import CSS in proper order for cascading
import './styles/globals.css';
import './styles/utilities.css';
import './styles/responsive.css';
import './App.css';

// Import cache management for consistent cross-device styling
// eslint-disable-next-line no-unused-vars
import cacheManager from './utils/cacheManagement';

// Core components - loaded immediately (above the fold)
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DynamicPageStyleLoader from './components/DynamicPageStyleLoader';
import SEOHead from './components/SEOHead';
import VisitorTracker from './components/VisitorTracker';
import CookieConsent from './components/CookieConsent';

// OPTIMIZED: Lazy load pages for better initial load performance
// Landing page - standalone (no Header/Footer)
const MovieMakerLanding = lazy(() => import('./pages/MovieMakerLanding'));
const MovieMakerAdmin = lazy(() => import('./pages/MovieMakerAdmin'));
const SoftwareShop = lazy(() => import('./pages/SoftwareShop'));
// Critical pages - loaded with priority
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Category = lazy(() => import('./pages/Category'));
const Cart = lazy(() => import('./pages/Cart'));

// Auth pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));

// User pages - loaded on demand
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Profile = lazy(() => import('./pages/Profile'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const UserSettings = lazy(() => import('./pages/UserSettings'));
const Orders = lazy(() => import('./pages/Orders'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));

// Content pages - loaded on demand
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const FAQ = lazy(() => import('./pages/FAQ'));

// Admin pages - loaded only when needed
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const TestDashboard = lazy(() => import('./pages/TestDashboard'));

// 404 page
const NotFound = lazy(() => import('./pages/NotFound'));

// Legal pages - rarely accessed, load on demand
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const DMCANotice = lazy(() => import('./pages/DMCANotice'));
const AccessibilityStatement = lazy(() => import('./pages/AccessibilityStatement'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));

// Lazy load non-critical components
const CacheDebugger = lazy(() => import('./components/CacheDebugger'));
const SupportChatWidget = lazy(() => import('./components/SupportChatWidget'));

// OPTIMIZED: Import contexts in dependency order
import { AuthProvider } from './contexts/AuthContext';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { CartProvider } from './contexts/CartContext';
import { LangProvider } from './contexts/LangContext';

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader" style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    fontSize: '1rem',
    color: 'var(--text-secondary, #666)'
  }}>
    <div className="loader-content" style={{ textAlign: 'center' }}>
      <div className="spinner" style={{
        width: '40px',
        height: '40px',
        border: '3px solid var(--border-color, #e0e0e0)',
        borderTop: '3px solid var(--primary-color, #007bff)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 1rem'
      }} />
      <p>Loading...</p>
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Phase D3: keep <html lang=".."> in sync with the LangContext state.
// Re-runs whenever 'mm-lang-changed' fires; the source of truth is the
// LangContext (localStorage + URL query + browser fallback).
function HtmlLangSync() {
  useEffect(() => {
    const apply = (e) => {
      const next = e && e.detail && e.detail.lang;
      if (next === 'tr' || next === 'en') {
        document.documentElement.lang = next;
        return;
      }
      try {
        const stored = localStorage.getItem('mm-lang');
        if (stored === 'tr' || stored === 'en') {
          document.documentElement.lang = stored;
          return;
        }
      } catch { /* ignore */ }
      const browser = (navigator.language || 'en').toLowerCase();
      document.documentElement.lang = browser.startsWith('tr') ? 'tr' : 'en';
    };
    apply();
    window.addEventListener('mm-lang-changed', apply);
    return () => window.removeEventListener('mm-lang-changed', apply);
  }, []);
  return null;
}

// Shop layout — wraps all non-landing pages with Header + Footer
const ShopLayout = () => (
  <>
    <Header />
    <main className="main-content">
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </main>
    <Footer />
  </>
);

function App() {
  // Cache debugger visibility state
  const [showCacheDebugger, setShowCacheDebugger] = useState(() => {
    return localStorage.getItem('showCacheDebugger') === 'true' || 
           new URLSearchParams(window.location.search).has('debug-cache');
  });

  useEffect(() => {
    // Listen for cache debugger toggle
    const handleToggleCacheDebugger = () => {
      setShowCacheDebugger(prev => {
        const newValue = !prev;
        localStorage.setItem('showCacheDebugger', newValue.toString());
        return newValue;
      });
    };

    const handleHideCacheDebugger = () => {
      setShowCacheDebugger(false);
      localStorage.setItem('showCacheDebugger', 'false');
    };

    // Keyboard shortcut to toggle cache debugger (Ctrl+Shift+D)
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        handleToggleCacheDebugger();
      }
    };

    window.addEventListener('toggleCacheDebugger', handleToggleCacheDebugger);
    window.addEventListener('hideCacheDebugger', handleHideCacheDebugger);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('toggleCacheDebugger', handleToggleCacheDebugger);
      window.removeEventListener('hideCacheDebugger', handleHideCacheDebugger);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <CartProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <LangProvider>
                <HtmlLangSync />
                <ScrollToTop />
                <SEOHead />
                <DynamicPageStyleLoader />
                <VisitorTracker />
                <div className="App fade-in">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Landing page — standalone, no Header/Footer. Bilingual (TR/EN). */}
                      <Route path="/" element={<MovieMakerLanding />} />
                      {/* Admin page — standalone, no Header/Footer */}
                      <Route path="/mm-admin" element={<MovieMakerAdmin />} />
                      {/* Software shop — standalone dark AI theme. Bilingual (TR/EN). */}
                      <Route path="/software" element={<SoftwareShop />} />

                    {/* All other pages wrapped with Header + Footer */}
                    <Route element={<ShopLayout />}>
                      <Route path="/products" element={<Products />} />
                      <Route path="/product/:slug" element={<ProductDetail />} />
                      <Route path="/category/:slug" element={<Category />} />
                      <Route path="/cart" element={<Cart />} />
                      
                      {/* Auth pages */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      
                      {/* User pages */}
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/order-confirmation" element={<OrderConfirmation />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/user-dashboard" element={<UserDashboard />} />
                      <Route path="/user-settings" element={<UserSettings />} />
                      <Route path="/orders" element={<Orders />} />
                      <Route path="/orders/:orderId" element={<TrackOrder />} />
                      <Route path="/track-order" element={<TrackOrder />} />
                      
                      {/* Content pages */}
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/faq" element={<FAQ />} />
                      
                      {/* Admin pages */}
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/admin/tests" element={<TestDashboard />} />
                      
                      {/* Legal pages */}
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/return-policy" element={<ReturnPolicy />} />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/dmca-notice" element={<DMCANotice />} />
                      <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
                      <Route path="/shipping-policy" element={<ShippingPolicy />} />

                      {/* 404 catch-all */}
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Routes>
                </Suspense>
              </div>
              
              {/* Cache Debugger - lazy loaded */}
              {showCacheDebugger && (
                <Suspense fallback={null}>
                  <CacheDebugger isVisible={showCacheDebugger} />
                </Suspense>
              )}
              
              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 3000,
                  style: {
                    background: 'var(--card-bg, #363636)',
                    color: 'var(--text-primary, #fff)',
                    border: '1px solid var(--border-color, #555)',
                    borderRadius: 'var(--border-radius-md, 8px)',
                    boxShadow: 'var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.15))',
                  },
                  success: {
                    style: {
                      background: 'var(--success-bg, #d4edda)',
                      color: 'var(--success-color, #155724)',
                      border: '1px solid var(--success-border, #c3e6cb)',
                    },
                  },
                  error: {
                    style: {
                      background: 'var(--danger-bg, #f8d7da)',
                      color: 'var(--danger-color, #721c24)',
                      border: '1px solid var(--danger-border, #f5c6cb)',
                    },
                  },
                  loading: {
                    style: {
                      background: 'var(--info-bg, #d1ecf1)',
                      color: 'var(--info-color, #0c5460)',
                      border: '1px solid var(--info-border, #bee5eb)',
                    },
                  },
                }}
              />

              {/* Cookie Consent Banner */}
              <CookieConsent />

              {/* Support chat assistant widget — global, all pages */}
              <Suspense fallback={null}>
                <SupportChatWidget />
              </Suspense>
            </LangProvider>
            </Router>
          </CartProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
